@file:OptIn(ExperimentalContracts::class)

package org.matthewo.api

import kotlinx.serialization.Serializable
import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.contract

class Schedule {
    @Serializable
    data class Match(
        val level: Level,
        val series: Int,
        val number: Int,
        val red: Alliance,
        val blue: Alliance,
        val scheduled: Long?,
        val played: Long?,
        val redWinProbability: Float,
        val scores: Scores?,
        val video: String?,
    ) {
        val order
            get() = number + when (level) {
                Level.QUAL -> 0
                else -> 1000 * level.ordinal + 100 * series
            }

        enum class Level(val title: String, val abbreviation: String) {
            QUAL("Qualification", "Q"),
            SEMIFINAL("Semifinal", "S"),
            FINAL("Final", "F"),
            PLAYOFF("Playoff", "P");

            companion object {
                val ORDER = entries.reversed()

                fun fromAbbreviation(abbreviation: String): Level? {
                    val lower = abbreviation.uppercase()
                    return entries.firstOrNull { it.abbreviation == lower }
                }
            }
        }
    }

    @Serializable
    data class Scores(
        val red: Int,
        val blue: Int,
        val redRP: List<Boolean>? = null,
        val blueRP: List<Boolean>? = null,
    )

    @Serializable
    data class Alliance(
        val number: Int?,
        val teams: List<Team>,
    )

    @Serializable
    data class Team(
        val number: Int,
        val name: String,
        val onField: Boolean = false,
        val surrogate: Boolean = false,
        val dq: Boolean = false,
    )
}

inline fun List<Schedule.Match>.forEachLevel(action: (level: Schedule.Match.Level, matches: List<Schedule.Match>, multipleSeries: Boolean, multipleNumbers: Boolean) -> Unit) {
    contract {
        callsInPlace(action)
    }
    val levels = groupBy { it.level }

    Schedule.Match.Level.ORDER.forEach { level ->
        val matches = levels[level] ?: return@forEach
        val multipleSeries = matches.map { it.series }.distinct().count() > 1
        val multipleNumbers = matches.map { it.number }.distinct().count() > 1
        action(level, matches, multipleSeries, multipleNumbers)
    }
}

inline fun List<Schedule.Match>.forEachLevel(action: (level: Schedule.Match.Level, matches: List<Schedule.Match>, formatMatchKey: (Schedule.Match) -> String) -> Unit) =
    forEachLevel { level, matches, multipleSeries, multipleNumbers ->
        val formatMatchKey = when {
            multipleSeries && multipleNumbers -> {
                match: Schedule.Match -> "${match.series}-${match.number}"
            }
            multipleSeries -> { match: Schedule.Match ->
                "${match.series}"
            }
            else -> { match: Schedule.Match ->
                "${match.number}"
            }
        }
        action(level, matches, formatMatchKey)
    }

inline fun List<Schedule.Match>.forEachLevel(action: (level: Schedule.Match.Level, matches: List<Schedule.Match>) -> Unit) =
    forEachLevel { level, matches, _ -> action(level, matches) }
